import { useState, useReducer, useEffect } from 'react';
import { serviceLogic } from '@web/lib/service-logic/index';
import { businessLogic } from '@web/lib/business-logic/index';
import { useMutation, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import {
  SunMedium,
  Home,
  Waves,
  Bath,
  ConciergeBell,
  Cat,
  Dog,
  TreePine,
} from '@repo/ui/components/ui/icons';
import {
  IFilterState,
  IFilterAction,
  IBusinessDetailFilterRaw,
} from '@web/types/business-details';

function reducer(state: IFilterState, action: IFilterAction) {
  switch (action.type) {
    case 'INIT':
      return action.payload;
    case 'SET_FILTER':
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }
}

const initialRaw: IBusinessDetailFilterRaw = {
  pet_type: [
    {
      label: 'Cat',
      value: 'cat',
      icon: <Cat />,
    },
    {
      label: 'Dog',
      value: 'dog',
      icon: <Dog />,
    },
  ],
  serviceType: [],
  serviceCategory: [],
};

const initialState: IFilterState = {
  selectedPetType: initialRaw.pet_type?.[0]?.value || 'cat',
  selectedServiceType: null,
  selectedServiceCategory: null,
  isOtherServiceCategory: false,
};

const getIcon = (name: string) => {
  switch (name) {
    case 'Day care':
      return <SunMedium />;
    case 'Pet Hotel':
      return <Home />;
    case 'Swimming':
      return <Waves />;
    case 'Grooming':
      return <Bath />;
    case 'Training':
      return <ConciergeBell />;
    default:
      return <TreePine />;
  }
};

export const useBusinessDetail = (id: string) => {
  const [raw, setRaw] = useState<IBusinessDetailFilterRaw>(initialRaw);
  const [filterState, dispatch] = useReducer(reducer, initialState);
  const approvedStatusMute = useMutation({
    mutationKey: ['approveBusiness'],
    mutationFn: (payload: { id: string; status: 'verified' | null }) =>
      businessLogic.approveBusiness(payload.id, payload.status),
  });
  const serviceQuery = useQuery({
    queryKey: ['services', filterState],
    queryFn: async () => {
      const service = await serviceLogic.getServiceByFilter(filterState, id);
      return service;
    },
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const business = useQuery({
    queryKey: ['business', id],
    queryFn: async () => {
      const businessInfo = await businessLogic.getBusinessById(id);
      const businessService = await serviceLogic.getServicesByBusinessId(id);
      return { businessInfo, businessService };
    },
    retry: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (business.isSuccess) {
      const { businessService } = business.data;

      const serviceTypePayload = _.chain(businessService.payload)
        .filter((el) => el.pet_type === filterState.selectedPetType)
        .map((service) => ({
          id: service.service_type_id || 0,
          name: service.service_type?.service_type_name || 'All Services',
          icon: getIcon(service.service_type?.service_type_name || ''),
        }))
        .uniqBy('id')
        .thru((services) => {
          if (services.length >= 1 && services?.[0]?.name !== 'All Services') {
            services.unshift({
              id: 0,
              name: 'All Services',
              icon: <TreePine />,
            });
          }
          return services;
        })
        .value();

      const categoryPayload = _.chain(businessService.payload)
        .filter((el) => el.pet_type === filterState.selectedPetType)
        .filter((el) => !!el.service_category)
        .map((service) => ({
          id: service.service_category_id,
          name: service.service_category?.category_name || '',
        }))
        .uniqBy('id')
        .value();

      setRaw((prevRaw) => ({
        ...prevRaw,
        serviceType: serviceTypePayload,
        serviceCategory: categoryPayload,
      }));

      dispatch({
        type: 'INIT',
        payload: {
          selectedPetType: filterState.selectedPetType,
          selectedServiceType: serviceTypePayload?.[0]?.id || null,
          selectedServiceCategory: categoryPayload?.[0]?.id || null,
          isOtherServiceCategory: false,
        },
      });
    }
  }, [filterState.selectedPetType, business.isSuccess, business.data]);

  useEffect(() => {
    if (business.isSuccess) {
      const { businessService } = business.data;

      const categoryPayload = _.chain(businessService.payload)
        .filter((el) => {
          if (
            filterState.selectedServiceType === null ||
            filterState.selectedServiceType === 0
          ) {
            return el.pet_type === filterState.selectedPetType;
          }
          return (
            el.pet_type === filterState.selectedPetType &&
            el.service_type_id === filterState.selectedServiceType
          );
        })
        .filter((el) => !!el.service_category)
        .map((service) => ({
          id: service.service_category_id,
          name: service.service_category?.category_name || '',
        }))
        .uniqBy('id')
        .value();

      setRaw((prevRaw) => ({
        ...prevRaw,
        serviceCategory: categoryPayload,
      }));

      dispatch({
        type: 'SET_FILTER',
        payload: {
          ...filterState,
          selectedServiceCategory: categoryPayload?.[0]?.id || null,
        },
      });
    }
  }, [
    filterState.selectedPetType,
    filterState.selectedServiceType,
    business.isSuccess,
    business.data,
  ]);

  return {
    raw,
    filterState,
    business,
    serviceQuery,
    approvedStatusMute,
    onUpdateSelectedFilter: (payload: IFilterState) => {
      dispatch({ type: 'SET_FILTER', payload });
    },
  };
};
